// SPDX-FileCopyrightText: 2026 Mads Ringblom
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The ⌘K command palette's commands, as data: go to a mode, a type, a
 * sample, a storey, a room; run an action; open an expert page. A command carries a
 * *target* rather than a callback, so the list is unit-testable and the
 * shell decides how to run it. Pure: no React, no DOM.
 */

import type { AuthUser, GlobalRole, Sample, SurveyType } from '../api/types';
import { globalRoleOf } from '../app/auth';
import { ADMIN_USERS_PATH, GROUPS_PATH, KONTO_PATH } from '../app/cases';
import { INDSTILLINGER_PATH, SEGMENTERING_PATH, SKABELONER_PATH } from '../app/links';
import { ALL_CASES_PATH } from '../app/navigation';
import type { ViewPreset } from '../viewport/cameraViews';
import { GEOMETRI_COMMANDS, type GeometriCommand } from './geometriKeys';
import type { ModeDef, ModeId } from './modes';
import { redigerHref } from './rediger';
import type { RoomInfo } from './rooms';
import type { Selection, ViewKind } from './selection';
import type { Storey } from './storeys';

/** Actions the shell knows how to run. */
export type PaletteAction =
  | 'sync'
  | 'add-manual'
  | 'export-csv'
  | 'generate-report'
  | 'toggle-theme'
  | 'settings'
  | 'pick-point'
  | 'toggle-projection';

export type CommandTarget =
  | { kind: 'mode'; mode: ModeId }
  | { kind: 'select'; mode: ModeId; patch: Partial<Selection> }
  | { kind: 'navigate'; to: string }
  /**
   * A page outside this case (the case list, Konto, …): a full page load to
   * an absolute path, never a router navigation, which would resolve it
   * inside the case (`/sager/<cid>/sager`).
   */
  | { kind: 'leave'; href: string }
  | { kind: 'action'; action: PaletteAction }
  /** A preset camera view of the Ground (Modelindstillinger › Visning). */
  | { kind: 'view'; preset: ViewPreset }
  /** A Rediger › Geometri command (its keymap registry, `geometriKeys.ts`), run by the editor. */
  | { kind: 'geometri'; command: GeometriCommand };

export type CommandGroup = 'Geometri' | 'Visning' | 'Model' | 'Type' | 'Prøve' | 'Etage' | 'Rum' | 'Handling' | 'Gå til' | 'Ekspert';

export interface Command {
  /** Unique within one list; also the option's DOM id suffix. */
  id: string;
  label: string;
  /** The right-hand column: the group, or a type's EAK code. */
  hint: string;
  group: CommandGroup;
  /** Extra words the filter matches but the row does not show. */
  keywords?: string;
  target: CommandTarget;
}

export interface PaletteSource {
  modes: readonly ModeDef[];
  types: readonly SurveyType[];
  samples: readonly Sample[];
  rooms: readonly RoomInfo[];
  /**
   * The storeys (stored, or derived — gap 7); absent or empty = no storey commands. A
   * storey command's patch sets only `storey`: the shell runs it through
   * `storeyPatch` so a room on another storey is dropped.
   */
  storeys?: readonly Storey[];
  experts: readonly { to: string; label: string; hint: string; keywords?: string }[];
  /** False for a viewer: write actions are left out. */
  canEdit: boolean;
  /** Rediger › Geometri is shown: its editor commands are offered (first). */
  geometri?: boolean;
  /** Who is signed in; absent = local mode (no login): only "Alle sager". */
  account?: AccountScope;
  /**
   * The part in focus and the mode whose evidence tile would show it; adds
   * "Del op <kode>". Absent = no part in focus.
   */
  focus?: { partCode: string; mode: ModeId };
}

/**
 * The SAM3 commands: the full Segmentering tool always (it is a Handling,
 * not only an expert page), and, with a part in focus and edit rights, its
 * evidence tile straight on Del op / 360°.
 */
export function segmentCommands(src: Pick<PaletteSource, 'focus' | 'canEdit'>): Command[] {
  const out: Command[] = [
    {
      id: 'seg:open',
      label: 'Åbn Segmentering',
      hint: 'Handling',
      group: 'Handling',
      keywords: 'sam3 segmentér del op maske tekstprompt konfidens kø etiket standardklasser',
      target: { kind: 'navigate', to: SEGMENTERING_PATH },
    },
  ];
  if (src.focus && src.canEdit) {
    const { partCode, mode } = src.focus;
    out.unshift({
      id: 'seg:part',
      label: `Del op ${partCode}`,
      hint: 'Handling',
      group: 'Handling',
      keywords: 'sam3 segmentér maske foto ressource fra markering',
      target: { kind: 'select', mode, patch: { partCode, ev: 'seg' } },
    });
  }
  return out;
}

/** The preset views, as the palette names them (same order as Modelindstillinger › Visning). */
const VIEW_COMMANDS: readonly { preset: ViewPreset; label: string; keywords: string }[] = [
  { preset: 'top', label: 'Se modellen ovenfra', keywords: 'top plan oppefra fugleperspektiv' },
  { preset: 'front', label: 'Se modellen forfra', keywords: 'front facade' },
  { preset: 'back', label: 'Se modellen bagfra', keywords: 'back bagside' },
  { preset: 'left', label: 'Se modellen fra venstre', keywords: 'left side' },
  { preset: 'right', label: 'Se modellen fra højre', keywords: 'right side' },
  { preset: 'bottom', label: 'Se modellen nedefra', keywords: 'bottom underside' },
];

/**
 * The Ground's viewer commands: Vælg punkt (source images), the camera
 * projection and the preset views — the old viewport's tools, in the palette.
 */
export function modelCommands(): Command[] {
  const out: Command[] = [
    {
      id: 'model:pick',
      label: 'Vælg punkt — vis kildebilleder',
      hint: 'Model',
      group: 'Model',
      keywords: 'pick point kildebillede billeder foto ramme frame synlighed',
      target: { kind: 'action', action: 'pick-point' },
    },
    {
      id: 'model:projection',
      label: 'Skift perspektiv / ortografisk',
      hint: 'Model',
      group: 'Model',
      keywords: 'kamera projektion ortho orthographic perspective',
      target: { kind: 'action', action: 'toggle-projection' },
    },
  ];
  for (const v of VIEW_COMMANDS) {
    out.push({ id: `model:view:${v.preset}`, label: v.label, hint: 'Model', group: 'Model', keywords: `kamera visning ${v.keywords}`, target: { kind: 'view', preset: v.preset } });
  }
  return out;
}

/** What the "Gå til" commands depend on: the session, not the case. */
export interface AccountScope {
  /** Signed in through a share link: no account and no case list of its own. */
  viaShareLink: boolean;
  /** The signed-in user's global role; admins also get "Brugere og roller". */
  globalRole: GlobalRole;
}

/** The `AccountScope` for a session: none in local mode (no signed-in user). */
export function accountScope(user: AuthUser | undefined, viaShareLink: boolean): AccountScope | undefined {
  if (viaShareLink) return { viaShareLink: true, globalRole: 'user' };
  return user ? { viaShareLink: false, globalRole: globalRoleOf(user) } : undefined;
}

/**
 * The way out of the case and to the account pages (main 82ac2a0's UserMenu
 * links, plus case sharing in Indstillinger). Local mode has only the case
 * list; a share link has none of them — it is bound to this one case.
 */
export function goToCommands(account: AccountScope | undefined): Command[] {
  if (account?.viaShareLink) return [];
  const go = (id: string, label: string, hint: string, keywords: string, target: CommandTarget): Command => ({
    id: `go:${id}`,
    label,
    hint,
    group: 'Gå til',
    keywords,
    target,
  });
  const out = [go('sager', 'Alle sager', 'Sager', 'sagsliste tilbage luk sag', { kind: 'leave', href: ALL_CASES_PATH })];
  if (!account) return out;
  out.push(
    go('deling', 'Deling og adgang', 'Sag', 'delingslink delt link medlemmer grupper indstillinger', {
      kind: 'navigate',
      to: INDSTILLINGER_PATH,
    }),
    go('konto', 'Konto', 'Konto', 'profil navn e-mail adgangskode', { kind: 'leave', href: KONTO_PATH }),
    go('grupper', 'Grupper', 'Konto', 'team hold medlemmer', { kind: 'leave', href: GROUPS_PATH }),
  );
  if (account.globalRole === 'admin') {
    out.push(go('brugere', 'Brugere og roller', 'Admin', 'administrator admin roller', { kind: 'leave', href: ADMIN_USERS_PATH }));
  }
  return out;
}

/**
 * Rediger's ways in (frontend-u0o.1), for editors only: Importér BIM-model
 * (the old dialog, now Rediger › Import on its Speckle source) and the three
 * sub-tabs — Scan & pose keeps Posegraf's task words.
 */
export function redigerCommands(canEdit: boolean): Command[] {
  if (!canEdit) return [];
  const go = (id: string, label: string, to: string, keywords: string, group: CommandGroup = 'Visning'): Command => ({
    id: `rediger:${id}`,
    label,
    hint: group === 'Handling' ? 'Handling' : 'Rediger',
    group,
    keywords,
    target: { kind: 'navigate', to },
  });
  return [
    go('import-bim', 'Importér BIM-model', redigerHref('import', { source: 'speckle' }), 'revit speckle ifc bim model import etager vægge plan', 'Handling'),
    go('import', 'Rediger › Import', redigerHref('import'), 'importér rtab-map rtabmap sessioner scanning historik'),
    go(
      'scan',
      'Rediger › Scan & pose',
      redigerHref('scan'),
      'posegraf pose graph kanter loop-lukning optimering justering registrering billedpar icp blokke sessioner scanning reparation',
    ),
    go('geometri', 'Rediger › Geometri', redigerHref('geometri'), 'bygningsmodel vægge åbninger døre vinduer rum tag dæk etager'),
  ];
}

/**
 * Rediger › Geometri's commands (frontend-u0o.9), while it is shown: every
 * entry of its keymap registry, so ⌘K finds a tool or a modify command by
 * name or task word ("forlæng" → Forlæng / trim) with its key as the hint.
 * Write commands are left out for a viewer; one whose slice has not landed
 * says "kommer snart".
 */
export function geometriCommands(canEdit: boolean): Command[] {
  return GEOMETRI_COMMANDS.filter((c) => canEdit || !c.write)
    .filter((c) => c.command !== 'cycle' && c.command !== 'cycle-back' && c.command !== 'escape')
    .map((c) => ({
      id: `geometri:${c.command}`,
      label: c.ready ? c.label : `${c.label} — kommer snart`,
      hint: c.keys || 'Geometri',
      group: 'Geometri' as const,
      keywords: `geometri ${c.keywords}`,
      target: { kind: 'geometri' as const, command: c.command },
    }));
}

/** Ressourcer's three faces (the top bar's ViewSwitch), as palette commands. */
const RESSOURCER_VIEWS: readonly { view: ViewKind; label: string; keywords: string }[] = [
  { view: 'model', label: 'Vis som model', keywords: '3d bygning punktsky scanning' },
  { view: 'tabel', label: 'Vis som tabel', keywords: 'liste rækker typer dele' },
  { view: 'galleri', label: 'Vis som galleri', keywords: 'kort billeder fotos ressourcebilleder' },
];

/** Every command, in the order an empty query shows them. */
export function buildCommands(src: PaletteSource): Command[] {
  const out: Command[] = [];
  // In the editor its own commands come first: they are what ⌘K is for there.
  if (src.geometri) out.push(...geometriCommands(src.canEdit));
  for (const m of src.modes) {
    out.push({ id: `mode:${m.id}`, label: `Gå til ${m.label}`, hint: 'Visning', group: 'Visning', target: { kind: 'mode', mode: m.id } });
  }
  out.push(...redigerCommands(src.canEdit).filter((c) => c.group === 'Visning'));
  for (const v of RESSOURCER_VIEWS) {
    out.push({
      id: `view:${v.view}`,
      label: v.label,
      hint: 'Visning',
      group: 'Visning',
      keywords: `ressourcer ${v.keywords}`,
      target: { kind: 'select', mode: 'ressourcer', patch: { view: v.view } },
    });
  }
  for (const t of src.types) {
    if (t.review_status === 'rejected') continue;
    out.push({
      id: `type:${t.id}`,
      label: t.name,
      hint: t.eak_code || 'Type',
      group: 'Type',
      keywords: `type ${t.eak_name} ${t.bim7aa_code}`,
      target: { kind: 'select', mode: 'ressourcer', patch: { typeId: t.id } },
    });
  }
  for (const s of src.samples) {
    out.push({
      id: `sample:${s.id}`,
      label: `${s.code} · ${s.title}`,
      hint: 'Prøve',
      group: 'Prøve',
      keywords: `prøve ${s.what}`,
      target: { kind: 'select', mode: 'miljoe', patch: { sampleId: s.id } },
    });
  }
  for (const s of src.storeys ?? []) {
    out.push({
      id: `storey:${s.id}`,
      label: `Fokusér ${s.name}`,
      hint: 'Etage',
      group: 'Etage',
      keywords: 'etage niveau',
      target: { kind: 'select', mode: 'overblik', patch: { storey: s.id } },
    });
  }
  for (const r of src.rooms) {
    if (r.id === null) continue;
    out.push({
      id: `room:${r.id}`,
      label: `Fokusér ${r.name}`,
      hint: 'Rum',
      group: 'Rum',
      keywords: 'rum',
      target: { kind: 'select', mode: 'overblik', patch: { room: r.id } },
    });
  }
  const actions: { action: PaletteAction; label: string; write: boolean; keywords?: string }[] = [
    { action: 'export-csv', label: 'Eksportér ressourcer (CSV)', write: false, keywords: 'download' },
    { action: 'add-manual', label: 'Tilføj type uden geometri', write: true, keywords: 'manuel ny' },
    { action: 'sync', label: 'Opret kortlægning fra instanser', write: true, keywords: 'synkronisér sync' },
    { action: 'generate-report', label: 'Generér rapport', write: true, keywords: 'pdf version' },
    { action: 'settings', label: 'Modelindstillinger', write: false, keywords: 'lag visning kamera pipeline ekspert' },
    { action: 'toggle-theme', label: 'Skift tema', write: false, keywords: 'mørk lys dark light' },
  ];
  for (const a of actions) {
    if (a.write && !src.canEdit) continue;
    out.push({ id: `action:${a.action}`, label: a.label, hint: 'Handling', group: 'Handling', keywords: a.keywords, target: { kind: 'action', action: a.action } });
  }
  out.push(...redigerCommands(src.canEdit).filter((c) => c.group === 'Handling'));
  // The retired Sagsdata mode's way in: its editor opens as a dialog over Overblik.
  if (src.canEdit) {
    out.push({
      id: 'edit:sag',
      label: 'Rediger sagsdata',
      hint: 'Handling',
      group: 'Handling',
      keywords: 'sag bygherre bbr matrikel kommune nedrivning etageareal frist noter',
      target: { kind: 'select', mode: 'overblik', patch: { edit: 'sag' } },
    });
  }
  out.push(...segmentCommands(src));
  out.push(...modelCommands());
  out.push(...goToCommands(src.account));
  // Skabeloner is a case tool every surveyor uses (the Tabel's columns, the
  // report's Ressourcetabel, the CSV export), not an expert page (frontend-r22.8).
  out.push({
    id: 'go:skabeloner',
    label: 'Skabeloner',
    hint: 'Sag',
    group: 'Gå til',
    keywords: 'skabelon felter kolonner csv eksport ressourcetabel redigér',
    target: { kind: 'navigate', to: SKABELONER_PATH },
  });
  for (const e of src.experts) {
    if (e.to === SKABELONER_PATH) continue;
    out.push({
      id: `expert:${e.to}`,
      label: `${e.label} (ekspert)`,
      hint: e.hint,
      group: 'Ekspert',
      keywords: e.keywords ? `ekspert ${e.keywords}` : 'ekspert',
      target: { kind: 'navigate', to: e.to },
    });
  }
  return out;
}

/** Lower case, diacritics folded (é → e), Danish letters kept (æ, ø, å). */
export function fold(text: string): string {
  // æ and ø never decompose; å does (a + ring), so it is parked first.
  return text
    .normalize('NFC')
    .toLowerCase()
    .replace(/å/g, '\u0001')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\u0001/g, 'å');
}

/**
 * How well `cmd` matches the folded query terms, lower is better; null for
 * no match. Every term must match somewhere (label, hint or keywords).
 * 0: the label starts with the query; 1: every term starts a label word;
 * 2: every term is in the label; 3: some term only matched hint/keywords.
 */
export function matchRank(cmd: Command, query: string): number | null {
  const q = fold(query.trim());
  if (!q) return 0;
  const terms = q.split(/\s+/);
  const label = fold(cmd.label);
  const rest = fold(`${cmd.hint} ${cmd.keywords ?? ''}`);
  if (!terms.every((t) => label.includes(t) || rest.includes(t))) return null;
  if (label.startsWith(q)) return 0;
  const words = label.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (terms.every((t) => words.some((w) => w.startsWith(t)))) return 1;
  if (terms.every((t) => label.includes(t))) return 2;
  return 3;
}

/** The commands matching `query`, best first; ties keep their list order. */
export function filterCommands(cmds: readonly Command[], query: string): Command[] {
  if (!query.trim()) return [...cmds];
  return cmds
    .map((cmd, i) => ({ cmd, i, rank: matchRank(cmd, query) }))
    .filter((x): x is { cmd: Command; i: number; rank: number } => x.rank !== null)
    .sort((a, b) => a.rank - b.rank || a.i - b.i)
    .map((x) => x.cmd);
}

/** The active row after an arrow key, clamped to the list. */
export function moveActive(index: number, delta: number, length: number): number {
  if (length <= 0) return 0;
  return Math.min(length - 1, Math.max(0, index + delta));
}
